import { fontFamilies } from "../../styles/typography";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import PageLoading from "../../routes/PageLoading";
import { Modal } from "antd";
import { motion } from "framer-motion";
import { modalStyles } from "../../utils/modalStyles";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import { fetchUserInfo, logout, setAccess } from "../../store/authSlice";
import { denyAccess } from "../../api/access";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../hooks/useViewport";
import ProfileDropdown from "../../components/ProfileDropdown";
import { RestaurantAccessModal } from "./components/RestaurantAccessModal";
import { useUserInfoRetry } from "./useUserInfoRetry";
import { AiIntegrationsModal } from "./components/AiIntegrationsModal";
import { AI_INTEGRATIONS_KEY, buildModules, type Module } from "./moduleList";
import { useConfirmAccess } from "../../hooks/useAccess";
import {
  blockIfLicenseReadOnly,
  isLicenseReadOnlyRole,
  useLicenseReadOnly,
} from "../../lib/licenseReadOnly";
import { APP_VERSION } from "../../lib/version";
import {
  ArrowUpOutlined,
  CloseOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import BrandLogo from "../../components/BrandLogo";

// Мащабна единица (като --u на ПИН екрана): 1px до 1440px viewport, после
// расте пропорционално (до 2px при ≥2880) — на голям монитор целият layout
// се уголемява, вместо да остава дребен с празни полета.
const mu = (n: number) => `calc(var(--mu, 1px) * ${n})`;
const MU_DEF = { "--mu": "clamp(1px, 0.06944vw, 2px)" } as React.CSSProperties;

const R = {
  bg: "var(--color-canvas)",
  surface: "var(--color-surface)",
  sidebarBg: "var(--color-sidebar)",
  border: "var(--color-shade-2)",
  borderMed: "var(--color-shade-3)",
  text: "var(--color-ink)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
  brand: "var(--color-brand)",
  brandDeep: "var(--color-brand-deep)",
  brandTint: "var(--color-brand-tint)",
  brandTint2: "var(--color-brand-tint2)",
  mono: fontFamilies.mono,
};

// Разбива n карти на редове с до maxPerRow карти; сам ред с 1 карта след
// ред с ≥3 се ребалансира (4 → 2+2, 7 → 3+2+2), за да няма самотна карта.
function bentoRows(n: number, maxPerRow: number): number[] {
  const rows: number[] = [];
  let left = n;
  while (left > 0) {
    const take = Math.min(maxPerRow, left);
    rows.push(take);
    left -= take;
  }
  if (
    rows.length >= 2 &&
    rows[rows.length - 1] === 1 &&
    rows[rows.length - 2] >= 3
  ) {
    rows[rows.length - 2] -= 1;
    rows[rows.length - 1] = 2;
  }
  return rows;
}

const Modules = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isNarrow, isTablet, isDesktop, isWide } = useViewport();

  const roles = useAppSelector((s) => s.auth.roles);
  const userId = useAppSelector((s) => s.auth.userId);
  const status = useAppSelector((s) => s.auth.status);
  const names = useAppSelector((s) => s.auth.names);
  const username = useAppSelector((s) => s.auth.username);
  const access = useAppSelector((s) => s.auth.access);
  const restaurantAccesses = useAppSelector((s) => s.auth.restaurantAccesses);
  const licenseReadOnly = useLicenseReadOnly();

  const confirmAccessMutation = useConfirmAccess();

  // 3 клика върху името — отваря модала „Тестов режим" (само при
  // архивиран ресторант И все още непотвърден access)
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [comingSoonModule, setComingSoonModule] = useState<string | null>(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);

  const handleGreetingClick = () => {
    // При вече включен тестов режим (access=true) няма какво да се въвежда —
    // трите клика работят отново чак след изход от режима (denyAccess).
    if (!restaurantAccesses || access) return;
    clickCountRef.current += 1;
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      clickCountRef.current = 0;
    }, 2000);
    if (clickCountRef.current >= 3) {
      clickCountRef.current = 0;
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      setAccessModalOpen(true);
    }
  };

  // Жив часовник за hero панела
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const locale = i18n.language?.startsWith("bg") ? "bg-BG" : "en-GB";
  const dateStr = now.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const timeStr = now.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });

  const modules: Module[] = useMemo(() => buildModules(t), [t]);

  const userRoleNames = useMemo(() => roles.map((role) => role.name), [roles]);

  const availableModules = useMemo(() => {
    return modules.filter((m) => {
      if (m.adminOnly && userId !== 1) return false;
      if (m.requiresAccess && restaurantAccesses && !access) return false;
      if (typeof m.roleName === "undefined") return true;
      return userRoleNames.includes(m.roleName);
    });
  }, [modules, userRoleNames, userId, restaurantAccesses, access]);

  useEffect(() => {
    if (status === "idle" && (!roles || roles.length === 0)) {
      dispatch(fetchUserInfo());
    }
  }, [dispatch, roles, status]);
  // Провалено първо зареждане (без роли) — ограничен брой повторни опити с
  // нарастващо изчакване, иначе екранът остава празен до изход.
  useUserInfoRetry({
    status,
    hasRoles: (roles?.length ?? 0) > 0,
    retry: () => void dispatch(fetchUserInfo()),
  });

  const roleModules = availableModules.filter(
    (m) => typeof m.roleName !== "undefined",
  );

  // Авто-пренасочването при единствен модул се прескача, ако се вижда
  // служебен (adminOnly) модул — иначе админ с една роля не би стигнал до него.
  const hasAdminModules = availableModules.some((m) => m.adminOnly);
  // ...и ако има модул, скрит само заради липсващ тестов режим — иначе Esc
  // от единствения видим модул би отскачал обратно и трите клика върху
  // името (входът за кода) не биха били достижими.
  const hasAccessHiddenModules = modules.some(
    (m) =>
      m.requiresAccess &&
      restaurantAccesses &&
      !access &&
      m.roleName !== undefined &&
      userRoleNames.includes(m.roleName),
  );

  if (
    status === "succeeded" &&
    roleModules.length === 1 &&
    !hasAdminModules &&
    !hasAccessHiddenModules &&
    // В режим „само преглед" единствен модул извън „Справки"/„Склад" би
    // отскачал обратно тук (AuthorizedRoute) — оставаме на плочките.
    (licenseReadOnly === null || isLicenseReadOnlyRole(roleModules[0].roleName))
  ) {
    // Докато се зареди кодът на модула, transition-ът задържа ТОЗИ екран:
    // `PageLoading` пуска веднага единствения spinner (`PageLoadingIndicator`,
    // като в AuthorizedRoute) — иначе празен екран, а собствен Spin тук би се
    // наложил върху него (docs/architecture.md §Код при отваряне).
    return (
      <>
        <PageLoading />
        <Navigate to={roleModules[0].path} replace />
      </>
    );
  }

  const displayName = names || username || "";

  const handleLogout = async () => {
    if (access) {
      try {
        await denyAccess();
      } catch (e) {
        console.error("Deny access error:", e);
      }
      dispatch(setAccess(false));
      navigate("/", { replace: true });
    } else {
      await dispatch(logout());
      navigate("/login", { replace: true });
    }
  };

  // Динамичен bento layout: редовете зависят от броя карти. Вдясно от hero
  // панела мястото е по-тясно → по-малко карти на ред от преди.
  const cardCount = availableModules.length;
  const maxPerRow = isNarrow ? 1 : isTablet ? 2 : isWide ? 3 : 2;
  const rows = bentoRows(cardCount, maxPerRow);
  // 12-колонен grid → span 12/len (len 1..4 → 12/6/4/3)
  const spans: number[] = [];
  rows.forEach((len) => {
    for (let i = 0; i < len; i++) spans.push(12 / len);
  });
  const rowCount = rows.length;
  const cardMinHeight =
    rowCount === 1 ? mu(230) : rowCount === 2 ? mu(205) : mu(185);

  // Поздравът с кликаемо име (easter egg-ът за тестовия режим). Името се
  // държи 1:1 като останалия текст (курсор, маркиране) — нищо не издава
  // скрития вход.
  const [greetBefore, greetAfter] = t("dashboard.greeting", {
    name: "__NAME__",
  }).split("__NAME__");

  const greetingName = (
    <span onClick={handleGreetingClick}>
      {displayName}
    </span>
  );
  // Двоен/троен клик по поздрава не маркира думата/реда (трите клика за
  // тестовия режим иначе го оцветяват); маркирането с плъзгане остава.
  const preventMultiClickSelection = (mouseEvent: React.MouseEvent) => {
    if (mouseEvent.detail > 1) mouseEvent.preventDefault();
  };

  const welcomePill = (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: `${mu(5)} ${mu(11)}`,
        borderRadius: 999,
        background: R.brandTint,
        border: "1px solid color-mix(in srgb, var(--color-brand) 20%, transparent)",
        color: "var(--color-brand-deep-ink)",
        fontSize: mu(11),
        fontWeight: 700,
        letterSpacing: 1.1,
        textTransform: "uppercase",
        width: "fit-content",
      }}
    >
      {t("modules.welcomeKicker")}
    </div>
  );

  const modulesGrid =
    availableModules.length === 0 ? (
      <div
        style={{
          padding: "28px 24px",
          background: R.surface,
          border: `1px solid ${R.border}`,
          borderRadius: 14,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: R.ink }}>
          {t("modules.noRoles")}
        </div>
        <div
          style={{
            marginTop: 8,
            fontSize: 13,
            color: R.textSoft,
            lineHeight: 1.55,
          }}
        >
          {t("modules.noRolesDesc")}
        </div>
      </div>
    ) : (
      <section
        style={{
          display: "grid",
          gridTemplateColumns: isNarrow ? "1fr" : "repeat(12, minmax(0, 1fr))",
          gap: isNarrow ? 10 : mu(16),
        }}
      >
        {availableModules.map((m, i) => {
          const span = spans[i] ?? 4;
          return (
            <ModuleCard
              key={m.key}
              module={m}
              index={i}
              variant={isNarrow ? "row" : "tile"}
              span={isNarrow ? undefined : span}
              large={!isNarrow && (span >= 6 || rowCount === 1)}
              minHeight={isNarrow ? undefined : cardMinHeight}
              comingSoonLabel={t("modules.comingSoonKicker")}
              onOpen={() => {
                if (m.key === AI_INTEGRATIONS_KEY) {
                  setAiModalOpen(true);
                } else if (m.comingSoon) {
                  setComingSoonModule(m.title);
                } else if (
                  !isLicenseReadOnlyRole(m.roleName) &&
                  blockIfLicenseReadOnly()
                ) {
                  // Режим „само преглед": плочката остава, модалът казва защо.
                  return;
                } else {
                  navigate(m.path);
                }
              }}
            />
          );
        })}
      </section>
    );

  return (
    <div
      className="font-sans h-screen flex flex-col overflow-hidden"
      style={{
        ...MU_DEF,
        background: R.bg,
        color: R.text,
      }}
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between flex-shrink-0"
        style={{
          padding: isNarrow ? "10px 16px" : "11px 28px",
          minHeight: isNarrow ? 48 : 60,
          borderBottom: `1px solid ${R.border}`,
          // Топъл кремав фон вместо плътно бяло — да не изпъква спрямо
          // sidebar-а/платното (както статус картата на ПИН екрана)
          background: access
            ? `radial-gradient(ellipse 40% 120% at 100% 50%, ${R.brandTint} 0%, transparent 70%), ${R.bg}`
            : R.bg,
          gap: 10,
        }}
      >
        <div
          className="flex items-center"
          style={{ gap: 10, minWidth: 0, flex: 1 }}
        >
          <BrandLogo className={isNarrow ? "size-[26px]" : "size-9"} />
          <div style={{ minWidth: 0, flex: 1, lineHeight: 1 }}>
            <h1
              style={{
                fontSize: isNarrow ? 16 : 18,
                fontWeight: 700,
                letterSpacing: -0.3,
                margin: 0,
                color: access ? "var(--color-brand-ink)" : R.ink,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                transition: "color 160ms",
              }}
            >
              {t("common.appName")}
            </h1>
            {!isNarrow && (
              <div
                style={{
                  fontSize: 12,
                  color: R.textSoft,
                  marginTop: 4,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  // 1.2 вместо наследеното 1: с overflow:hidden кирилските
                  // descender-и (р/у/д) иначе се режат
                  lineHeight: 1.2,
                }}
              >
                {t("common.restaurantSystem")}
              </div>
            )}
          </div>
        </div>

        <div
          className="flex items-center"
          style={{ gap: isNarrow ? 4 : 10, flexShrink: 0 }}
        >
          <ProfileDropdown
            displayName={displayName}
            username={username ?? ""}
            access={access}
            isNarrow={isNarrow}
            onLogout={handleLogout}
          />
        </div>
      </div>

      {/* Main */}
      {isDesktop ? (
        /* Desktop: страничен hero панел + модули вдясно */
        <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
          <aside
            style={{
              position: "relative",
              overflow: "hidden",
              width: "clamp(320px, 28vw, 640px)",
              flexShrink: 0,
              background: R.sidebarBg,
              borderRight: `1px solid ${R.border}`,
              display: "flex",
              flexDirection: "column",
              padding: `${mu(40)} ${mu(36)} ${mu(32)}`,
            }}
          >
            {/* Decorative amber glows */}
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                background: `
                  radial-gradient(ellipse 55% 50% at 15% 12%, ${R.brandTint} 0%, transparent 60%),
                  radial-gradient(ellipse 70% 55% at 85% 92%, ${R.brandTint2} 0%, transparent 65%)
                `,
                pointerEvents: "none",
              }}
            />
            {/* Subtle grid overlay */}
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: `linear-gradient(${R.border} 1px, transparent 1px), linear-gradient(90deg, ${R.border} 1px, transparent 1px)`,
                backgroundSize: "48px 48px",
                maskImage:
                  "radial-gradient(ellipse 70% 60% at 50% 50%, #000 30%, transparent 80%)",
                opacity: 0.6,
                pointerEvents: "none",
              }}
            />

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: "easeOut" }}
              style={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 18,
              }}
            >
              {welcomePill}
              <h1
                onMouseDown={preventMultiClickSelection}
                style={{
                  margin: 0,
                  fontSize: mu(36),
                  fontWeight: 700,
                  letterSpacing: -1.2,
                  lineHeight: 1.12,
                  color: R.ink,
                }}
              >
                {greetBefore}
                {greetingName}
                {greetAfter}
              </h1>
              <p
                style={{
                  margin: 0,
                  fontSize: mu(14),
                  color: R.textSoft,
                  lineHeight: 1.6,
                  maxWidth: mu(300),
                }}
              >
                {t("dashboard.subtitle")}
              </p>
            </motion.div>

            <div style={{ flex: 1 }} />

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.55, delay: 0.15, ease: "easeOut" }}
              style={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 14,
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: R.mono,
                    fontVariantNumeric: "tabular-nums",
                    fontSize: mu(44),
                    fontWeight: 700,
                    letterSpacing: -1,
                    color: R.ink,
                    lineHeight: 1,
                  }}
                >
                  {timeStr}
                </div>
                <div
                  style={{
                    marginTop: 8,
                    fontSize: mu(13),
                    fontWeight: 500,
                    color: R.textSoft,
                  }}
                >
                  {dateStr}
                </div>
              </div>

              {APP_VERSION && (
                <div
                  style={{
                    borderTop: `1px solid ${R.borderMed}`,
                    paddingTop: 14,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      fontFamily: R.mono,
                      fontSize: mu(10.5),
                      fontWeight: 600,
                      letterSpacing: 0.3,
                      color: R.textMuted,
                      padding: `${mu(4)} ${mu(9)}`,
                      borderRadius: 999,
                      border: `1px solid ${R.borderMed}`,
                    }}
                  >
                    v{APP_VERSION}
                  </div>
                </div>
              )}
            </motion.div>
          </aside>

          <main
            className="flex-1 min-h-0 overflow-y-auto"
            style={{ minWidth: 0 }}
          >
            <div
              style={{
                minHeight: "100%",
                display: "flex",
                flexDirection: "column",
                padding: `${mu(28)} ${mu(36)} ${mu(32)}`,
              }}
            >
              {/* Спейсъри: при малко карти ги центрират леко над средата */}
              <div style={{ flex: "0.8 0 0" }} />
              {modulesGrid}
              <div style={{ flex: "1.2 0 0" }} />
            </div>
          </main>
        </div>
      ) : (
        /* Tablet + телефон: топла hero лента над модулите */
        <main className="flex-1 min-h-0 overflow-y-auto">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: isNarrow ? 14 : 18,
              padding: isNarrow ? "14px 14px 24px" : "22px 28px 32px",
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              style={{
                position: "relative",
                overflow: "hidden",
                background: R.sidebarBg,
                border: `1px solid ${R.border}`,
                borderRadius: 18,
                padding: isNarrow ? "18px 18px" : "24px 26px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
              }}
            >
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `
                    radial-gradient(ellipse 50% 90% at 8% 15%, ${R.brandTint} 0%, transparent 60%),
                    radial-gradient(ellipse 55% 95% at 95% 90%, ${R.brandTint2} 0%, transparent 65%)
                  `,
                  pointerEvents: "none",
                }}
              />
              <div
                style={{
                  position: "relative",
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: isNarrow ? 6 : 10,
                }}
              >
                {!isNarrow && welcomePill}
                <h1
                  onMouseDown={preventMultiClickSelection}
                  style={{
                    margin: 0,
                    fontSize: isNarrow ? 21 : 26,
                    fontWeight: 700,
                    letterSpacing: -0.7,
                    lineHeight: 1.15,
                    color: R.ink,
                  }}
                >
                  {greetBefore}
                  {greetingName}
                  {greetAfter}
                </h1>
                <p
                  style={{
                    margin: 0,
                    fontSize: isNarrow ? 12.5 : 13.5,
                    color: R.textSoft,
                    lineHeight: 1.5,
                  }}
                >
                  {t("dashboard.subtitle")}
                </p>
              </div>

              <div
                style={{
                  position: "relative",
                  flexShrink: 0,
                  textAlign: "right",
                  display: isNarrow ? "none" : "block",
                }}
              >
                <div
                  style={{
                    fontFamily: R.mono,
                    fontVariantNumeric: "tabular-nums",
                    fontSize: 30,
                    fontWeight: 700,
                    letterSpacing: -0.6,
                    color: R.ink,
                    lineHeight: 1,
                  }}
                >
                  {timeStr}
                </div>
                <div
                  style={{
                    marginTop: 7,
                    fontSize: 12,
                    fontWeight: 500,
                    color: R.textSoft,
                  }}
                >
                  {dateStr}
                </div>
              </div>
            </motion.div>

            {modulesGrid}
          </div>
        </main>
      )}

      <ComingSoonModal
        open={comingSoonModule !== null}
        moduleName={comingSoonModule ?? ""}
        isNarrow={isNarrow}
        onClose={() => setComingSoonModule(null)}
        t={t}
      />

      <AiIntegrationsModal
        open={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
      />

      <RestaurantAccessModal
        open={accessModalOpen}
        onClose={() => setAccessModalOpen(false)}
        loading={confirmAccessMutation.isPending}
        onCodeSubmit={async (code) => {
          try {
            await confirmAccessMutation.mutateAsync({ code2: code });
            setAccessModalOpen(false);
          } catch {
            // модалът остава отворен при грешка
          }
        }}
      />
    </div>
  );
};

function ModuleCard({
  module: m,
  index,
  variant,
  span,
  large,
  minHeight,
  comingSoonLabel,
  onOpen,
}: {
  module: Module;
  index: number;
  variant: "tile" | "row";
  span?: number;
  large: boolean;
  minHeight?: number | string;
  comingSoonLabel: string;
  onOpen: () => void;
}) {
  const [hover, setHover] = useState(false);
  const { Icon } = m;

  const comingSoonPill = m.comingSoon ? (
    <div
      style={{
        padding: variant === "row" ? "3px 8px" : `${mu(3)} ${mu(8)}`,
        borderRadius: 999,
        background: R.brandTint,
        border: "1px solid color-mix(in srgb, var(--color-brand) 20%, transparent)",
        color: "var(--color-brand-deep-ink)",
        fontSize: variant === "row" ? 10 : mu(10),
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {comingSoonLabel}
    </div>
  ) : null;

  // Голямата икона в chip с amber градиент
  const iconChip = (
    <div
      style={{
        width: variant === "row" ? 46 : mu(large ? 56 : 48),
        height: variant === "row" ? 46 : mu(large ? 56 : 48),
        borderRadius: variant === "row" ? 12 : mu(14),
        background: `linear-gradient(135deg, ${R.brandTint} 0%, ${R.brandTint2} 100%)`,
        border: "1px solid color-mix(in srgb, var(--color-brand) 16%, transparent)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon
        style={{
          fontSize: variant === "row" ? 20 : mu(large ? 26 : 22),
          color: "var(--color-brand-deep-ink)",
        }}
      />
    </div>
  );

  const commonMotion = {
    initial: { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: {
      duration: 0.4,
      delay: 0.08 + index * 0.05,
      ease: "easeOut" as const,
    },
    whileHover: { y: -2 },
  };

  if (variant === "row") {
    return (
      <motion.div
        {...commonMotion}
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
          }
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "14px 16px",
          background: R.surface,
          border: `1px solid ${R.border}`,
          borderRadius: 14,
          cursor: "pointer",
          outline: "none",
        }}
      >
        {iconChip}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: -0.3,
                color: R.ink,
                lineHeight: 1.2,
              }}
            >
              {m.title}
            </h3>
            {comingSoonPill}
          </div>
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 12.5,
              color: R.textSoft,
              lineHeight: 1.45,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {m.desc}
          </p>
        </div>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: R.brandTint,
            color: "var(--color-brand-deep-ink)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <ArrowUpOutlined
            style={{ fontSize: 12, transform: "rotate(45deg)" }}
          />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      {...commonMotion}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        overflow: "hidden",
        padding: large
          ? `${mu(26)} ${mu(28)}`
          : `${mu(22)} ${mu(24)}`,
        background: R.surface,
        border: `1px solid ${hover ? "color-mix(in srgb, var(--color-brand) 35%, transparent)" : R.border}`,
        borderRadius: mu(18),
        cursor: "pointer",
        transition: "border-color 160ms ease, box-shadow 160ms ease",
        boxShadow: hover
          ? "0 12px 28px rgba(17,17,17,0.07), 0 3px 8px rgba(17,17,17,0.04)"
          : "none",
        display: "flex",
        flexDirection: "column",
        gap: mu(16),
        minHeight,
        outline: "none",
        gridColumn: span ? `span ${span}` : undefined,
      }}
    >
      {/* Amber glow горе-дясно — засилва се на hover */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse 70% 65% at 100% 0%, ${R.brandTint} 0%, transparent 60%)`,
          opacity: hover ? 1 : large ? 0.85 : 0.5,
          transition: "opacity 200ms ease",
          pointerEvents: "none",
        }}
      />
      {/* Watermark икона долу-дясно */}
      <Icon
        aria-hidden
        style={{
          position: "absolute",
          right: -16,
          bottom: -20,
          fontSize: mu(large ? 120 : 100),
          color: hover ? "color-mix(in srgb, var(--color-brand) 9%, transparent)" : "color-mix(in srgb, var(--color-brand) 5%, transparent)",
          transform: "rotate(-10deg)",
          transition: "color 200ms ease",
          pointerEvents: "none",
        }}
      />

      <div
        className="flex items-start justify-between"
        style={{ position: "relative", gap: 10 }}
      >
        {iconChip}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {comingSoonPill}
          <div
            style={{
              width: mu(30),
              height: mu(30),
              borderRadius: mu(8),
              background: hover ? R.brandTint : "transparent",
              color: hover ? "var(--color-brand-deep-ink)" : R.textMuted,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 160ms ease, color 160ms ease",
            }}
          >
            <ArrowUpOutlined
              style={{ fontSize: mu(13), transform: "rotate(45deg)" }}
            />
          </div>
        </div>
      </div>

      <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
        <h3
          style={{
            margin: 0,
            fontSize: mu(large ? 20 : 18),
            fontWeight: 700,
            letterSpacing: -0.4,
            color: R.ink,
            lineHeight: 1.2,
          }}
        >
          {m.title}
        </h3>
        <p
          style={{
            marginTop: 8,
            marginBottom: 0,
            fontSize: mu(large ? 14 : 13),
            color: R.textSoft,
            lineHeight: 1.55,
            display: "-webkit-box",
            WebkitLineClamp: large ? 3 : 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            maxWidth: large ? mu(560) : "none",
          }}
        >
          {m.desc}
        </p>
      </div>
    </motion.div>
  );
}

function ComingSoonModal({
  open,
  moduleName,
  isNarrow,
  onClose,
  t,
}: {
  open: boolean;
  moduleName: string;
  isNarrow: boolean;
  onClose: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={isNarrow ? undefined : 440}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div style={{ background: R.surface, position: "relative" }}>
        {/* Decorative amber glow */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 180,
            background: `radial-gradient(ellipse 80% 80% at 50% 0%, ${R.brandTint} 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />

        {/* Close chip — amber themed */}
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            height: 30,
            width: 30,
            borderRadius: 8,
            background: "transparent",
            border: "none",
            color: "var(--color-brand-deep-ink)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 120ms, color 120ms",
            zIndex: 2,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = R.brandTint;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
        >
          <CloseOutlined style={{ fontSize: 14 }} />
        </button>

        {/* Body */}
        <div
          style={{
            position: "relative",
            padding: isNarrow ? "36px 22px 22px" : "44px 32px 26px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 14,
          }}
        >
          <div
            style={{
              width: 68,
              height: 68,
              borderRadius: 18,
              background: R.brandTint,
              border: `1px solid ${R.brandTint2}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--color-brand-deep-ink)",
              boxShadow: "0 10px 28px color-mix(in srgb, var(--color-brand) 18%, transparent)",
            }}
          >
            <ClockCircleOutlined style={{ fontSize: 30 }} />
          </div>

          <div
            style={{
              fontSize: isNarrow ? 24 : 28,
              fontWeight: 700,
              letterSpacing: -0.8,
              color: "var(--color-brand-deep-ink)",
              lineHeight: 1.1,
              marginTop: 6,
            }}
          >
            {t("modules.comingSoonKicker")}
          </div>

          <div
            style={{
              fontSize: isNarrow ? 13.5 : 14,
              fontWeight: 600,
              color: R.ink,
              lineHeight: 1.4,
              letterSpacing: -0.1,
              maxWidth: 360,
            }}
          >
            {t("modules.comingSoonTitle", { module: moduleName })}
          </div>

          <div
            style={{
              fontSize: 12.5,
              fontWeight: 500,
              color: R.textSoft,
              lineHeight: 1.5,
              maxWidth: 340,
            }}
          >
            {t("modules.comingSoonDesc")}
          </div>
        </div>

        {/* CTA */}
        <div
          style={{
            position: "relative",
            padding: isNarrow ? "4px 16px 18px" : "4px 24px 22px",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              height: 46,
              width: "100%",
              borderRadius: 12,
              border: "none",
              background: R.brand,
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: -0.2,
              cursor: "pointer",
              transition: "background 120ms",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = R.brandDeep;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = R.brand;
            }}
          >
            {t("modules.comingSoonCta")}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default Modules;
